/**
 * Domain and subdomain detection utilities
 * Handles multi-tenant routing based on subdomain
 */

export type PortalType = 'landing' | 'app' | 'admin' | 'company';

export interface DomainConfig {
  subdomain: string | null;
  portalType: PortalType;
  companyId: string | null;
  isCustomDomain: boolean;
}

/**
 * Get the subdomain from the current hostname
 * Examples:
 *   - kessan.cl → null
 *   - app.kessan.cl → "app"
 *   - admin.kessan.cl → "admin"
 *   - maqenergy.kessan.cl → "maqenergy"
 *   - localhost → null
 */
export const getSubdomain = (): string | null => {
  const hostname = window.location.hostname;
  
  // Development: localhost or IP
  if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    return null;
  }
  
  // Main domain without subdomain (kessan.cl)
  const parts = hostname.split('.');
  if (parts.length === 2) {
    return null;
  }
  
  // Subdomain exists (app.kessan.cl, admin.kessan.cl, maqenergy.kessan.cl)
  if (parts.length >= 3) {
    return parts[0];
  }
  
  return null;
};

/**
 * Determine the portal type based on subdomain
 */
export const getPortalType = (): PortalType => {
  const subdomain = getSubdomain();
  
  // No subdomain = landing page
  if (!subdomain) {
    return 'landing';
  }
  
  // Admin portal (prod and dev)
  if (subdomain === 'admin' || subdomain === 'admin-dev') {
    return 'admin';
  }
  
  // App portal (prod and dev)
  if (subdomain === 'app' || subdomain === 'app-dev') {
    return 'app';
  }

  // API subdomain — not a portal
  if (subdomain === 'api' || subdomain === 'api-dev') {
    return 'landing';
  }
  
  // Company-specific portal (maqenergy, etc.)
  return 'company';
};

/**
 * Get company ID from subdomain (for company portals)
 * Returns null if not a company portal
 */
export const getCompanyIdFromSubdomain = (): string | null => {
  const portalType = getPortalType();
  
  if (portalType !== 'company') {
    return null;
  }
  
  return getSubdomain();
};

/**
 * Get complete domain configuration
 */
export const getDomainConfig = (): DomainConfig => {
  const subdomain = getSubdomain();
  const portalType = getPortalType();
  const companyId = getCompanyIdFromSubdomain();
  const isCustomDomain = portalType === 'company';
  
  return {
    subdomain,
    portalType,
    companyId,
    isCustomDomain,
  };
};

/**
 * Check if current portal is admin
 */
export const isAdminPortal = (): boolean => {
  return getPortalType() === 'admin';
};

/**
 * Check if current portal is company-specific
 */
export const isCompanyPortal = (): boolean => {
  return getPortalType() === 'company';
};

/**
 * Check if current portal is the main app
 */
export const isAppPortal = (): boolean => {
  return getPortalType() === 'app';
};

/**
 * Check if current portal is the landing page
 */
export const isLandingPage = (): boolean => {
  return getPortalType() === 'landing';
};

/**
 * Get the base domain (without subdomain)
 * Examples:
 *   - app.kessan.cl → kessan.cl
 *   - maqenergy.kessan.cl → kessan.cl
 *   - localhost → localhost
 */
export const getBaseDomain = (): string => {
  const hostname = window.location.hostname;
  
  // Development
  if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    return hostname;
  }
  
  const parts = hostname.split('.');
  
  // Already base domain
  if (parts.length === 2) {
    return hostname;
  }
  
  // Extract base domain (last 2 parts)
  if (parts.length >= 3) {
    return parts.slice(-2).join('.');
  }
  
  return hostname;
};

/**
 * Build URL for a specific subdomain
 * Examples:
 *   - buildSubdomainUrl('app') → https://app.kessan.cl
 *   - buildSubdomainUrl('admin') → https://admin.kessan.cl
 *   - buildSubdomainUrl(null) → https://kessan.cl
 */
export const buildSubdomainUrl = (subdomain: string | null, path: string = ''): string => {
  const baseDomain = getBaseDomain();
  const protocol = window.location.protocol;
  const port = window.location.port ? `:${window.location.port}` : '';
  
  let url: string;
  
  if (subdomain) {
    url = `${protocol}//${subdomain}.${baseDomain}${port}`;
  } else {
    url = `${protocol}//${baseDomain}${port}`;
  }
  
  if (path) {
    url += path.startsWith('/') ? path : `/${path}`;
  }
  
  return url;
};

/**
 * Redirect to a specific subdomain
 */
export const redirectToSubdomain = (subdomain: string | null, path: string = ''): void => {
  const url = buildSubdomainUrl(subdomain, path);
  window.location.href = url;
};

/**
 * Redirect to app portal
 */
export const redirectToApp = (path: string = ''): void => {
  redirectToSubdomain('app', path);
};

/**
 * Redirect to admin portal
 */
export const redirectToAdmin = (path: string = ''): void => {
  redirectToSubdomain('admin', path);
};

/**
 * Redirect to landing page
 */
export const redirectToLanding = (path: string = ''): void => {
  redirectToSubdomain(null, path);
};
